import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../api';

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = [
    {
      title: "Welcome to TIFL",
      subtitle: "Your Child's Development Companion",
      body: "Monitor and support your child's growth with expert tools and personalized guidance.",
      image: "/image 3.png",
    },
    {
      title: "Track Key Milestones",
      subtitle: "",
      body: "Easily record and track your child's physical, cognitive, and social milestones.",
      image: "/image 4.png",
    },
    {
      title: "Early Identification Resources",
      subtitle: "",
      body: "Our tools help you identify any potential delays in speech, motor skills, or social interactions.",
      image: "/image 5.png",
    }
  ];

  const handleSlideChange = (newSlide) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(newSlide);
      setIsTransitioning(false);
    }, 200);
  };

  const handleNext = async () => {
    if (currentSlide < slides.length - 1) {
      handleSlideChange(currentSlide + 1);
    } else {
      try {
        const accessToken = localStorage.getItem('access');
        
        await api.post('/accounts/onboarding/complete/', {}, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
      } finally {
        navigate('/client-dashboard');
      }
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) handleSlideChange(currentSlide - 1);
  };

  const handleDotClick = (index) => {
    if (index !== currentSlide) handleSlideChange(index);
  };

  const handleSkip = async () => {
    try {
      const accessToken = localStorage.getItem('access');
      
      await api.post('/accounts/onboarding/complete/', {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    } finally {
      navigate('/client-dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-pink-50/30 to-purple-50/20 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative BG */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-pink-200/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="w-full max-w-sm md:max-w-2xl lg:max-w-3xl relative z-10">
        {/* Skip (top-right) */}
        <div className="absolute top-2 right-2">
          <button
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700 font-medium transition-colors text-sm md:text-base"
          >
            Skip
          </button>
        </div>

        {/* STACKED LAYOUT: Image -> Dots -> Text -> Buttons -> Progress */}
        <div className="flex flex-col items-center">

          {/* Image with blush background */}
          <div className="relative w-72 h-72 sm:w-80 sm:h-80 md:w-96 md:h-96 mb-6">
<div className="absolute inset-0 flex items-center justify-center">
  {/* Main blush layer – bigger and brighter */}
  <div className="absolute w-[28rem] h-[28rem] sm:w-[32rem] sm:h-[32rem] md:w-[36rem] md:h-[36rem] bg-[#FDE9EB] rounded-full opacity-80 blur-2xl animate-blob"></div>

  {/* Mid glow layer – subtle motion depth */}
  <div className="absolute w-[24rem] h-[24rem] sm:w-[28rem] sm:h-[28rem] md:w-[32rem] md:h-[32rem] bg-[#FDE9EB]/80 rounded-full opacity-60 blur-[90px] animate-blob animation-delay-2000"></div>

  {/* Outer soft halo – expanded faint glow */}
  <div className="absolute w-[30rem] h-[30rem] sm:w-[34rem] sm:h-[34rem] md:w-[40rem] md:h-[40rem] bg-[#FDE9EB]/70 rounded-full opacity-50 blur-[120px] animate-blob animation-delay-4000"></div>
</div>


            {/* Main image */}
            <div
              className={`relative z-10 w-full h-full transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'
                }`}
            >
              <img
                src={slides[currentSlide].image}
                alt={slides[currentSlide].title}
                className="w-full h-full object-contain drop-shadow-xl select-none"
                draggable="false"
              />
            </div>
          </div>


          {/* Dots */}
          <div className="flex gap-3 mb-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`transition-all duration-500 cursor-pointer ${index === currentSlide
                  ? 'w-8 h-2 bg-gradient-to-r from-[#1E3A5F] to-[#2A4B73] rounded-full shadow-lg'
                  : 'w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400'
                  }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Text */}
          <div className={`text-center px-4 md:px-6 mb-8 transition-all duration-300 transform ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1E3A5F] mb-3 tracking-tight">
              {slides[currentSlide].title}
            </h1>

            {slides[currentSlide].subtitle && (
              <p className="text-lg md:text-xl font-medium text-[#1E3A5F]/80 mb-4">
                {slides[currentSlide].subtitle}
              </p>
            )}

            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-xl mx-auto">
              {slides[currentSlide].body}
            </p>
          </div>

          {/* Nav Buttons */}
          <div className="flex gap-4 items-center w-full max-w-sm md:max-w-md">
            <button
              onClick={handlePrevious}
              className={`p-3 rounded-xl border-2 border-gray-200 transition-all ${currentSlide === 0
                ? 'opacity-0 pointer-events-none'
                : 'opacity-100 hover:border-[#1E3A5F] hover:bg-gray-50'
                }`}
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5 text-[#1E3A5F]" />
            </button>

            <button
              onClick={handleNext}
              className="flex-1 bg-gradient-to-r from-[#1E3A5F] to-[#2A4B73] text-white py-4 px-8 rounded-xl font-semibold text-base hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
            </button>

            {currentSlide < slides.length - 1 && (
              <button
                onClick={handleNext}
                className="p-3 rounded-xl border-2 border-[#1E3A5F] bg-[#1E3A5F]/5 hover:bg-[#1E3A5F]/10 transition-all"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5 text-[#1E3A5F]" />
              </button>
            )}
          </div>

          {/* Progress */}
          <div className="w-full max-w-sm md:max-w-md mt-6 bg-gray-200 h-1 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#1E3A5F] to-[#2A4B73] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
